import React, { useContext } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import tokenVideoSrcMp4 from './tokenVideoMp4.mp4';
import tokenVideoSrcWebm from './tokenVideoWebm.webm';
import Dashboard from '../../components/Dashboard';
import DriverLeaderboard from '../../components/DriverLeaderboard';
import { LeaderboardStep } from '../../components/DriverLeaderboard/buildSteps';
import { WEATHER_BOARD_ID } from '../../components/WallBoardForm/constants';
import WeatherBoard from '../../components/WeatherBoard';
import Row from '../../components/Common/Row';
import isWeatherBoard from './isWeatherBoard';
import isDriverLeaderboard from './isDriverLeaderboard';
import isDashboardSlide from './isDashboardSlide';
import { ButtonPosition, SignOutButton } from './WallboardButtons';
import Button from '../../kingpin/atoms/Button';
import WallboardContext from '../../contexts/WallboardContext';
import PoweredByFleetOps from 'components/PoweredByFleetOps';

export const PoweredByPosition = styled.div`
  position: absolute;
  bottom: 16px;
  right: 28px;
  z-index: 999999999999;
`;

const WallboardWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  height: 100vh;
`;

// Also rendered by the screenshot renderer's print screen
// (screens/PrintWallboardShow), which pins driver leaderboards to one step.
export const WallboardDashboard = ({
  dashboard,
  leaderboardStepIndex,
  onLeaderboardStepsResolved,
}: {
  dashboard: WallboardSlide;
  leaderboardStepIndex?: number;
  onLeaderboardStepsResolved?: (steps: LeaderboardStep[]) => void;
}) => {
  const { dashboardZoom } = useContext(WallboardContext);

  // Weather boards and the bespoke Driver Leaderboard manage their own
  // full-screen layout, so they are never zoomed like a dashboard slide.
  const isBespokeSlide =
    isWeatherBoard(dashboard) || isDriverLeaderboard(dashboard);

  return (
    <div
      style={{
        flex: 1,
        height: '100vh',
        zoom: isBespokeSlide ? 1 : dashboardZoom,
      }}
    >
      <WallboardWrapper>
        <Row style={{ justifyContent: 'flex-end' }}>
          <video width="2px" height="2px" autoPlay muted loop>
            <source src={tokenVideoSrcWebm} type="video/webm" />
            <source src={tokenVideoSrcMp4} type="video/mp4" />
          </video>
        </Row>
        {isDriverLeaderboard(dashboard) && (
          <DriverLeaderboard
            slide={dashboard}
            key={dashboard.id}
            pinnedStepIndex={leaderboardStepIndex}
            onStepsResolved={onLeaderboardStepsResolved}
          />
        )}
        {isWeatherBoard(dashboard) && <WeatherBoard key={WEATHER_BOARD_ID} />}
        {isDashboardSlide(dashboard) && (
          <Dashboard dashboard={dashboard} key={dashboard.id} />
        )}
      </WallboardWrapper>
    </div>
  );
};

const WallBoard = ({
  dashboard,
  isCloseVisible,
  isWallboardUser,
}: {
  dashboard?: WallboardSlide;
  isCloseVisible: boolean;
  isWallboardUser: boolean;
}) => (
  <React.Fragment>
    {!isWallboardUser && isCloseVisible && <CloseButton />}
    {isWallboardUser && isCloseVisible && <SignOutButton />}
    {dashboard && (
      <WallboardDashboard dashboard={dashboard} key={dashboard.id} />
    )}
    {/* The Driver Leaderboard renders the mark inside its own (scaled) footer
        so it lines up with the slide's content. */}
    {!(dashboard && isDriverLeaderboard(dashboard)) && (
      <PoweredByPosition>
        <PoweredByFleetOps />
      </PoweredByPosition>
    )}
  </React.Fragment>
);

const CloseButton = () => {
  const navigate = useNavigate();
  const onClick = () => {
    navigate(-1);
  };

  return (
    <ButtonPosition>
      <Button size={'Medium'} type={'Primary'} onClick={onClick} icon="cross" />
    </ButtonPosition>
  );
};

export default WallBoard;
